import { DateTime } from 'luxon';

import { freightBookingWizardMessages } from './freight-booking-wizard-messages';
import { validationMessages } from '../../address/validation-messages';
import { getMissingRequiredHazmatFieldsForCommodity } from '../../ltl/hazmat-validation';

import type { FreightBookingFormState, WizardStepId } from './freight-booking-wizard-types';
import type { LtlAddress, LtlCommodity, LtlHandlingUnit } from '../../types/ltl';
import type { MessageDescriptor } from 'react-intl';

export type WizardFieldError = {
  path: string;
  message: MessageDescriptor;
  values?: Record<string, string | number>;
  /**
   * True when the field holds a value that is present but not allowed, as opposed to
   * merely missing. Consumers surface these immediately — an unusable value can arrive
   * from a hydrated saved quote the user never typed, so waiting for them to visit the
   * step would leave the form silently unquotable.
   */
  isInvalidValue?: boolean;
};

/**
 * Required address fields for the Ship To step. Exported so ShipToStep can consume
 * the same list for its `requiredFields` asterisks, keeping both in sync from a
 * single source of truth.
 *
 * The `satisfies` clause ensures every entry is a valid `LtlAddress` key — a rename
 * of `LtlAddress` that breaks a match here becomes a compile error rather than a
 * silently dead check. Note: AddressForm field ids happen to match these LtlAddress
 * keys exactly; if that ever diverges a separate id-alias layer will be needed.
 */
export const REQUIRED_SHIP_TO_ADDRESS_FIELDS = [
  'name',
  'line1',
  'city',
  'state',
  'postalCode',
  'phone',
  'email',
] as const satisfies readonly (keyof LtlAddress)[];

/** FPS rejects quotes with a pickup date more than this many days after today. */
export const MAX_SHIP_DATE_DAYS_AHEAD = 60;

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

const { required } = validationMessages;

/** Returns a single-element error if the string value is falsy, otherwise empty. */
const requireText = (path: string, value: string | undefined | null): WizardFieldError[] => (value ? [] : [{ path, message: required }]);

/** Returns a single-element error if the number is not strictly > 0, otherwise empty. */
const requirePositive = (path: string, value: number): WizardFieldError[] => (value > 0 ? [] : [{ path, message: required }]);

/**
 * True when `dateStr` represents a date before today (calendar-day granular, timezone-agnostic
 * string comparison on `yyyy-MM-dd`). Returns false for missing/empty values — callers that need
 * to require a value use `requireShipDate` instead.
 */
export const isShipDateInPast = (dateStr: string | undefined | null): boolean => {
  const dayStr = dateStr?.split('T')[0] ?? '';
  return Boolean(dayStr) && dayStr < DateTime.now().toFormat('yyyy-MM-dd');
};

/** Validates the ship date: required, must not be in the past, and must not exceed the carrier's day cap. */
const requireShipDate = (value: string | undefined | null): WizardFieldError[] => {
  if (!value) return [{ path: 'shipmentDate', message: required }];
  if (isShipDateInPast(value)) {
    return [{ path: 'shipmentDate', message: freightBookingWizardMessages.shipDateInPastError, isInvalidValue: true }];
  }
  const parsed = DateTime.fromISO(value).startOf('day');
  if (parsed.isValid && parsed.diff(DateTime.now().startOf('day'), 'days').days > MAX_SHIP_DATE_DAYS_AHEAD) {
    return [
      {
        path: 'shipmentDate',
        message: freightBookingWizardMessages.shipDateTooFarAhead,
        values: { days: MAX_SHIP_DATE_DAYS_AHEAD },
        isInvalidValue: true,
      },
    ];
  }
  return [];
};

// ---------------------------------------------------------------------------
// Per-step rule functions
// ---------------------------------------------------------------------------

const getShipFromErrors = (state: FreightBookingFormState): WizardFieldError[] => [
  ...requireText('selectedShipFromId', state.selectedShipFromId),
  ...requireShipDate(state.shipmentDate),
  ...(state.willArrangePickupSeparately
    ? []
    : [...requireText('pickupReadyTime', state.pickupReadyTime), ...requireText('pickupCloseTime', state.pickupCloseTime)]),
];

/**
 * Destination fields the rating request actually needs. Deliberately narrower than
 * REQUIRED_SHIP_TO_ADDRESS_FIELDS: mapAddressToBackend drops `email` entirely, and
 * rating only needs the destination geography, so gating rates on the full ship-to
 * set would leave the rates panel empty while contact details are still being typed.
 */
const QUOTE_REQUIRED_ADDRESS_FIELDS = ['city', 'state', 'postalCode'] as const satisfies readonly (keyof LtlAddress)[];

const getAddressErrors = (state: FreightBookingFormState, fields: readonly (keyof LtlAddress)[]): WizardFieldError[] =>
  fields.flatMap((field) => requireText(`destinationAddress.${field}`, state.destinationAddress[field] as string | null | undefined));

const getShipToErrors = (state: FreightBookingFormState): WizardFieldError[] => getAddressErrors(state, REQUIRED_SHIP_TO_ADDRESS_FIELDS);

const getCommodityHazmatErrors = (commodity: LtlCommodity, prefix: string): WizardFieldError[] =>
  [...getMissingRequiredHazmatFieldsForCommodity(commodity)].map((field) => ({
    path: `${prefix}.hazmatInfo.${field}`,
    message: required,
  }));

const getCommodityRateErrors = (commodity: LtlCommodity, commPrefix: string): WizardFieldError[] => [
  ...requirePositive(`${commPrefix}.weight`, commodity.weight),
  ...requireText(`${commPrefix}.freightClass`, commodity.freightClass),
];

const getHandlingUnitRateErrors = (handlingUnit: LtlHandlingUnit, handlingUnitIndex: number): WizardFieldError[] => {
  const handlingUnitPrefix = `handlingUnits.${handlingUnitIndex}`;
  const dimensionErrors: WizardFieldError[] = [
    ...requirePositive(`${handlingUnitPrefix}.length`, handlingUnit.length),
    ...requirePositive(`${handlingUnitPrefix}.width`, handlingUnit.width),
    ...requirePositive(`${handlingUnitPrefix}.height`, handlingUnit.height),
  ];
  if (handlingUnit.commodities.length === 0) {
    return [...dimensionErrors, { path: `${handlingUnitPrefix}.commodities`, message: required }];
  }
  return [
    ...dimensionErrors,
    ...handlingUnit.commodities.flatMap((commodity, commIndex) =>
      getCommodityRateErrors(commodity, `${handlingUnitPrefix}.commodities.${commIndex}`),
    ),
  ];
};

const getHandlingUnitErrors = (handlingUnit: LtlHandlingUnit, handlingUnitIndex: number): WizardFieldError[] => {
  const handlingUnitPrefix = `handlingUnits.${handlingUnitIndex}`;
  return [
    ...getHandlingUnitRateErrors(handlingUnit, handlingUnitIndex),
    ...handlingUnit.commodities.flatMap((commodity, commIndex) =>
      getCommodityHazmatErrors(commodity, `${handlingUnitPrefix}.commodities.${commIndex}`),
    ),
  ];
};

const getHandlingUnitsRateErrors = (state: FreightBookingFormState): WizardFieldError[] =>
  state.handlingUnits.length === 0
    ? [{ path: 'handlingUnits', message: required }]
    : state.handlingUnits.flatMap(getHandlingUnitRateErrors);

const getHandlingUnitsErrors = (state: FreightBookingFormState): WizardFieldError[] =>
  state.handlingUnits.length === 0 ? [{ path: 'handlingUnits', message: required }] : state.handlingUnits.flatMap(getHandlingUnitErrors);

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Errors that block fetching rates — a strict subset of getAllErrors.
 * Intentionally does not require contact fields (name, company, etc.) because
 * the rating request only needs ship-from, shipmentDate, destination geography,
 * and handling-unit data. Gating rates on the full booking-complete set would
 * leave the rates panel empty while the user is still filling in contact details.
 */
export const getQuoteBlockingErrors = (state: FreightBookingFormState): WizardFieldError[] => [
  ...requireText('selectedShipFromId', state.selectedShipFromId),
  ...requireShipDate(state.shipmentDate),
  ...getAddressErrors(state, QUOTE_REQUIRED_ADDRESS_FIELDS),
  ...getHandlingUnitsRateErrors(state),
];

/** True when the form has enough data to fetch carrier rates. */
export const getIsReadyToQuote = (state: FreightBookingFormState): boolean => getQuoteBlockingErrors(state).length === 0;

/** Returns all field-level errors for the given step. */
export const getStepErrors = (step: WizardStepId, formState: FreightBookingFormState): WizardFieldError[] => {
  switch (step) {
    case 'ship-from':
      return getShipFromErrors(formState);
    case 'ship-to':
      return getShipToErrors(formState);
    case 'handling-units':
      return getHandlingUnitsErrors(formState);
    case 'additional-details':
      return [];
  }
};

/**
 * Given a `fieldErrors` map (path → message) and a path segment prefix,
 * returns a new map containing only entries whose key starts with `prefix.`,
 * with the `prefix.` stripped from each key.
 *
 * The trailing dot is appended internally so callers pass a plain segment
 * (`'handlingUnits.0'`) and can't accidentally omit or double-add the separator.
 *
 * Example: sliceFieldErrors({ 'handlingUnits.0.length': 'Required' }, 'handlingUnits.0')
 *   → { 'length': 'Required' }
 */
export const sliceFieldErrors = (fieldErrors: Record<string, string>, prefix: string): Record<string, string> => {
  const dotPrefix = `${prefix}.`;
  return Object.fromEntries(
    Object.entries(fieldErrors)
      .filter(([path]) => path.startsWith(dotPrefix))
      .map(([path, msg]) => [path.slice(dotPrefix.length), msg]),
  );
};

export const WIZARD_STEP_IDS = ['ship-from', 'ship-to', 'handling-units', 'additional-details'] as const satisfies readonly WizardStepId[];

/** Returns all field-level errors across every step. */
export const getAllErrors = (formState: FreightBookingFormState): WizardFieldError[] =>
  WIZARD_STEP_IDS.flatMap((step) => getStepErrors(step, formState));

/** True when no step has any required-field violations. */
export const getIsFormComplete = (formState: FreightBookingFormState): boolean => getAllErrors(formState).length === 0;

/**
 * True when the given step has at least one required-field violation.
 * Signature is unchanged from the original so call sites in `FreightBookingWizard`
 * need no changes in Phase 1.
 */
export const hasStepErrors = (step: WizardStepId, formState: FreightBookingFormState): boolean => getStepErrors(step, formState).length > 0;

/**
 * Path used for the rate-missing error so consumers can discriminate it from
 * field-level gaps without parsing message text.
 */
export const SELECTED_RATE_PATH = 'selectedRate';

export type BookValidationContext = {
  selectedRate: { productTransactionId?: string } | null;
  /** False when the last re-quote returned Expired / NoMatch / failed; omit or true when available. */
  isRateAvailable?: boolean;
};

/** Superset of getAllErrors — additionally requires a selected rate that is still available. */
export const getBookBlockingErrors = (formState: FreightBookingFormState, context: BookValidationContext): WizardFieldError[] => [
  ...getAllErrors(formState),
  ...(context.selectedRate && context.isRateAvailable !== false ? [] : [{ path: SELECTED_RATE_PATH, message: required }]),
];

export const isReadyToBook = (formState: FreightBookingFormState, context: BookValidationContext): boolean =>
  getBookBlockingErrors(formState, context).length === 0;

/** Index into WIZARD_STEP_IDS of the earliest step with required-field violations, or -1 if none. */
const findFirstInvalidStepIndex = (formState: FreightBookingFormState): number =>
  WIZARD_STEP_IDS.findIndex((step) => hasStepErrors(step, formState));

/** The earliest step with required-field violations, or null when the whole form passes. */
export const getFirstInvalidStep = (formState: FreightBookingFormState): WizardStepId | null =>
  WIZARD_STEP_IDS[findFirstInvalidStepIndex(formState)] ?? null;

/**
 * Steps strictly before the first one with required-field violations — every step the user has
 * fully satisfied in order. A later step that happens to be valid is deliberately excluded, so it
 * renders as pending rather than pre-checked.
 */
export const getStepsBeforeFirstInvalid = (formState: FreightBookingFormState): readonly WizardStepId[] => {
  const firstInvalidIndex = findFirstInvalidStepIndex(formState);
  return firstInvalidIndex === -1 ? WIZARD_STEP_IDS : WIZARD_STEP_IDS.slice(0, firstInvalidIndex);
};

/**
 * Steps up to and including the first one with required-field violations. Callers treat these as
 * visited: the user has either passed through them or is being landed on them, so their errors are
 * safe to surface. Steps past the first gap stay silent until the user reaches them.
 */
export const getStepsThroughFirstInvalid = (formState: FreightBookingFormState): readonly WizardStepId[] => {
  const before = getStepsBeforeFirstInvalid(formState);
  return before.length === WIZARD_STEP_IDS.length ? WIZARD_STEP_IDS : WIZARD_STEP_IDS.slice(0, before.length + 1);
};
